/**
 * Schema Renderer
 *
 * Reads a JSON validation schema from a selected payment method button
 * (data-validation-schema) and dynamically renders the required extra fields
 * into [data-schema-fields] inside any payment modal (withdrawal OR deposit).
 *
 * Generated markup per field (prefix defaults to "wallet-withdrawal"; pass
 * "wallet-deposit" as the second constructor argument for the deposit flow):
 *   <div class="{prefix}-schema-field-group">
 *     <div class="field (text|select|radio|checkbox) {prefix}-schema-field" data-schema-field-wrapper="code">
 *       <input|select|div[role=group] data-schema-field="code">
 *     </div>
 *     <span class="field__validation-message" data-error-for="code"></span>
 *   </div>
 *
 * Supported frontend_component values: "text" | "select" | "radio" | "checkbox"
 * Supported validation rules:          required, min_length, max_length, regex
 * Dependent option lists:              a select/radio with `depends_on` offers only the
 *                                      options whose `parent_values` hold the value of
 *                                      that other field (e.g. a coin's networks)
 * Drawn with icons (`icons: true`):    a select becomes a searchable icon list, a radio a
 *                                      row of icon buttons (schema-icon-choices.js); the
 *                                      native select / radios still hold the value
 * The mobile number (`system`,         drawn as registration's phone field with the dial
 * `target: mobilePhone`):              code fixed at +1 (schema-phone-field.js); its value
 *                                      is the full "+1…" number
 *
 * Exported as SchemaRenderer (canonical) and WithdrawalSchemaRenderer (alias
 * kept for backwards compatibility with the withdrawal flow controller).
 *
 * @package betbeast
 */

import { FloatingLabel } from '../../auth-flow-components/floating-label.js';
import {
    CLASSES,
    applyFieldState,
    clearFieldState as clearFieldStateDOM,
    getOrCreateErrorElement
} from '../../modules/field-state-manager.js';
import { IconSelect, buildIconPills, optionCode, readChoiceTexts } from './schema-icon-choices.js';
import { SchemaPhoneField, isPhoneField } from './schema-phone-field.js';

export class SchemaRenderer {

    constructor( modalElement, prefix = 'wallet-withdrawal' ) {
        this.modal     = modalElement;
        this.container = modalElement?.querySelector( '[data-schema-fields]' ) ?? null;
        this.schema    = null;
        this.prefix    = prefix;
        // Per-player seed values, keyed by a field's `target` (falling back to its
        // `code`). Empty unless a flow calls setInitialValues(), so flows that don't
        // pre-fill behave exactly as before.
        this.initialValues = {};
        // Called after every render of the fields, with no arguments. Null unless a flow
        // sets it (the deposit's phone change notice); withdrawals leave it unset.
        this.onRender = null;
        // Parent field code → the fields whose options depend on its value (`depends_on`).
        this.dependents = new Map();
        // Field code → the SchemaPhoneField drawn for it; rebuilt with every render.
        this.phoneFields = new Map();
        // Texts printed in the modal for the "Select …" placeholder and the icon controls;
        // read on first use (see schema-icon-choices.js, readChoiceTexts).
        this.texts = null;
        this._text = ( key, values ) => {
            if ( ! this.texts ) this.texts = readChoiceTexts( this.modal );
            return this.texts( key, values );
        };
    }

    // -------------------------------------------------------------------------
    // Public API
    // -------------------------------------------------------------------------

    /**
     * Seed the values used to pre-fill controls on the next render.
     *
     * A schema field carrying `system: true` names the profile key it maps to in
     * `target`; that key is looked up here. Called once per flow — the values are
     * player-level, not method-level, so they survive method switching.
     *
     * @param {Object} values Map of profile key → value.
     */
    setInitialValues( values ) {
        // An empty PHP array encodes as [], not {} — treat it as "nothing seeded".
        const usable = values && typeof values === 'object' && ! Array.isArray( values );
        this.initialValues = usable ? values : {};
    }

    /**
     * Parse the schema from the selected method button and render its fields.
     *
     * @param {HTMLElement|null} methodButton
     */
    renderForMethod( methodButton ) {
        if ( ! this.container ) return;

        const raw = methodButton?.getAttribute( 'data-validation-schema' ) ?? null;

        if ( ! raw ) {
            this.schema = null;
            this.clear();
            return;
        }

        try {
            this.schema = JSON.parse( raw );
        } catch ( e ) {
            console.error( 'SchemaRenderer: invalid schema JSON', e );
            this.schema = null;
            this.clear();
            return;
        }

        this._render();
    }

    /**
     * Validate all rendered fields. Focuses the first failing field.
     *
     * @returns {{ valid: boolean, message?: string }}
     */
    validate() {
        if ( ! this.schema?.fields ) return { valid: true };

        this._clearAllErrors();

        for ( const field of this.schema.fields ) {
            const input   = this.container.querySelector( `[data-schema-field="${ field.code }"]` );
            const wrapper = this.container.querySelector( `[data-schema-field-wrapper="${ field.code }"]` );
            if ( ! input || ! this._isActive( field.code ) ) continue;

            const error = this._applyFieldValidation( field, wrapper, input );
            if ( error ) {
                this._focusField( wrapper, input );
                return { valid: false, message: error };
            }
        }

        return { valid: true };
    }

    /**
     * Collect current field values as { fieldCode: value }.
     *
     * @returns {Object}
     */
    collectValues() {
        if ( ! this.schema?.fields || ! this.container ) return {};

        return Object.fromEntries(
            this.schema.fields
                .map( field => {
                    // An inactive dependent field is not part of this payment — never sent.
                    if ( ! this._isActive( field.code ) ) return null;

                    const value = this._readValue( field );
                    return null === value ? null : [ field.code, value ];
                } )
                .filter( Boolean )
        );
    }

    /**
     * @returns {boolean} True when the active schema has at least one field.
     */
    hasFields() {
        return !! this.schema?.fields?.length;
    }

    // -------------------------------------------------------------------------
    // Rendering
    // -------------------------------------------------------------------------

    _render() {
        this.clear();
        if ( ! this.schema?.fields ) return;

        this.schema.fields.forEach( field => {
            this.container.appendChild( this._buildFieldGroup( field ) );
        } );

        this.container.style.display = 'flex';
        new FloatingLabel( this.container );
        this.phoneFields.forEach( phone => phone.updateHint() );

        this._wireDependencies();

        if ( typeof this.onRender === 'function' ) {
            this.onRender();
        }
    }

    clear() {
        if ( ! this.container ) return;
        this.phoneFields.clear();
        this.container.innerHTML     = '';
        this.container.style.display = 'none';
    }

    /**
     * Build one group: bordered wrapper + error element below it.
     *
     * @param {Object} field
     * @returns {HTMLElement}
     */
    _buildFieldGroup( field ) {
        const group   = document.createElement( 'div' );
        group.className = `${ this.prefix }-schema-field-group`;

        // A dependent field waits for its parent: _wireDependencies() shows it once the
        // parent holds a value it has options for. Inline style, not `hidden` — the group's
        // own CSS sets `display`, which would override the attribute.
        if ( field.depends_on ) {
            this._setGroupActive( group, false );
        }

        const wrapper = this._buildWrapper( field );
        const errEl   = this._buildErrorEl( field.code );

        wrapper._errorElement = errEl;
        wrapper.appendChild( this._buildControl( field ) );
        this._attachListeners( field, wrapper );

        // The error goes right under the control, as in the registration form; the note below
        // stays put while it comes and goes.
        group.appendChild( wrapper );
        group.appendChild( errEl );

        // Optional note beneath the control, for a field that changes something beyond this
        // payment — the mobile number is the one the account uses for SMS, 2FA and KYC, so
        // the player has to be told that filling it in here rewrites it there too.
        if ( field.description ) {
            const desc = document.createElement( 'p' );
            desc.className   = `${ this.prefix }-schema-field-description`;
            desc.textContent = field.description;
            group.appendChild( desc );
        }

        return group;
    }

    /**
     * @param {Object} field
     * @returns {HTMLElement}
     */
    _buildWrapper( field ) {
        const cssType = isPhoneField( field )
            ? 'tel'
            : ( { select: 'select', radio: 'radio', checkbox: 'checkbox' }[ field.frontend_component ] ?? 'text' );
        const wrapper = document.createElement( 'div' );
        wrapper.className = `field ${ cssType } ${ this.prefix }-schema-field`;
        wrapper.setAttribute( 'data-schema-field-wrapper', field.code );
        return wrapper;
    }

    /**
     * @param {Object} field
     * @returns {HTMLSelectElement|HTMLInputElement}
     */
    _buildControl( field ) {
        const control = this._buildControlElement( field );
        // The element a dependent field's rebuild replaces. Not [data-schema-field]: for an
        // icon select that is the native select inside the control, not the control itself.
        control.setAttribute( 'data-schema-control', field.code );
        return control;
    }

    /**
     * @param {Object} field
     * @returns {HTMLElement}
     */
    _buildControlElement( field ) {
        // Radio and checkbox build their own group element; id/name/data-attr
        // are applied to the individual inputs inside, not the group wrapper.
        if ( field.frontend_component === 'radio' ) {
            return field.icons ? this._buildIconPills( field ) : this._buildRadioGroup( field );
        }
        if ( field.frontend_component === 'checkbox' ) {
            return this._buildCheckbox( field );
        }

        // The mobile number's input gets no maxLength: it shows the masked national number,
        // which is longer than the "+1…" value the length rules count.
        const phone = isPhoneField( field );
        const input = field.frontend_component === 'select'
            ? this._buildSelect( field )
            : ( phone ? document.createElement( 'input' ) : this._buildInput( field ) );

        input.id   = `schema_field_${ field.code }`;
        input.name = `schema_field_${ field.code }`;
        input.setAttribute( 'data-schema-field', field.code );
        if ( field.required ) input.required = true;

        // Drawn with icons: a searchable list over the same select, which keeps the value.
        if ( field.frontend_component === 'select' && field.icons ) {
            return new IconSelect( input, field, this._text ).root;
        }

        // The mobile number: registration's phone field, its dial code fixed.
        if ( phone ) {
            const control = new SchemaPhoneField( input, field, this._initialFor( field ), value => this._getFieldError( field, value ) );
            this.phoneFields.set( field.code, control );
            return control.root;
        }

        return input;
    }

    /**
     * The player's current value for a field, or '' when there is none to show.
     * `target` names the profile key; a field without one falls back to its own code,
     * so nothing changes for schemas that don't use the profile.
     *
     * A stored value that the field's own rules reject is NOT pre-filled — the field is
     * left empty instead. This is the case that matters rather than an edge one: the
     * malformed numbers this integration exists to correct are in the record right now,
     * and a province an operator typed as "Ontario" matches no option. An empty required
     * field with a placeholder is a clear instruction; a pre-filled value the field itself
     * rejects reads as something the player got wrong.
     *
     * @param {Object} field
     * @returns {string}
     */
    _initialFor( field ) {
        const key = field.target ?? field.code;
        const val = this.initialValues[ key ];
        const str = ( val === null || val === undefined ) ? '' : String( val );

        if ( str === '' ) return '';

        // Closed lists check membership; everything else goes through the same rules the
        // field is validated with on submit. _getFieldError() returns '' for an empty
        // value before the required check, so a seed is only ever length/pattern-tested.
        const options = field.options ?? [];
        if ( options.length ) {
            return options.some( o => o.value === str ) ? str : '';
        }

        return this._getFieldError( field, str ) ? '' : str;
    }

    /**
     * @param {Object} field
     * @returns {HTMLSelectElement}
     */
    _buildSelect( field ) {
        const select = document.createElement( 'select' );
        select.setAttribute( 'placeholder', field.label );

        const options = field.options ?? [];
        // The player's own value wins over the schema's static default. _initialFor() has
        // already discarded anything that doesn't match an option, so a stale profile value
        // arrives here as '' and the static default keeps its turn.
        const initial = this._initialFor( field );
        const seeded  = initial !== '';

        const blank       = document.createElement( 'option' );
        blank.value       = '';
        blank.textContent = this._text( 'select', { label: field.label } );
        blank.disabled    = true;
        // Only pre-select the blank placeholder when nothing else claims the selection.
        blank.selected    = ! seeded && ! options.some( o => o.default );
        select.appendChild( blank );

        options.forEach( opt => {
            const option       = document.createElement( 'option' );
            option.value       = opt.value;
            option.textContent = opt.label;
            if ( seeded ? opt.value === initial : opt.default ) option.selected = true;
            select.appendChild( option );
        } );

        return select;
    }

    /**
     * @param {Object} field
     * @returns {HTMLInputElement}
     */
    _buildInput( field ) {
        const input         = document.createElement( 'input' );
        input.type          = 'text';
        input.placeholder   = field.label;
        if ( field.max_length ) input.maxLength = field.max_length;
        // Pre-filled from the player's profile where one is seeded. Never trusted —
        // validate() re-checks it on submit like anything the player typed.
        input.value         = this._initialFor( field );
        return input;
    }

    /**
     * Build a radio-group wrapper. Each radio carries data-schema-field so
     * collectValues() can query 'input[type="radio"]:checked' inside the wrapper.
     *
     * @param {Object} field
     * @returns {HTMLElement}
     */
    _buildRadioGroup( field ) {
        const group = document.createElement( 'div' );
        group.className = 'schema-radio-group';
        group.setAttribute( 'data-schema-field', field.code );
        group.setAttribute( 'role', 'radiogroup' );
        group.setAttribute( 'aria-label', field.label );

        const options = field.options ?? [];
        // Same precedence as _buildSelect: the player's value wins over the static default,
        // and _initialFor() has already discarded one that matches no option.
        const initial = this._initialFor( field );
        const seeded  = initial !== '';

        options.forEach( opt => {
            const label = document.createElement( 'label' );
            label.className = 'schema-radio-label';

            const radio       = document.createElement( 'input' );
            radio.type        = 'radio';
            radio.name        = `schema_field_${ field.code }`;
            radio.value       = opt.value;
            if ( seeded ? opt.value === initial : opt.default ) radio.checked = true;
            if ( field.required ) radio.required = true;

            label.appendChild( radio );
            label.append( ` ${ opt.label }` );
            group.appendChild( label );
        } );

        return group;
    }

    /**
     * Build a radio field drawn with icons: one button per option (see buildIconPills).
     *
     * @param {Object} field
     * @returns {HTMLElement}
     */
    _buildIconPills( field ) {
        // Same precedence as _buildRadioGroup: the player's value, else the static default.
        const initial = this._initialFor( field );
        const checked = '' !== initial
            ? initial
            : ( ( field.options ?? [] ).find( opt => opt.default )?.value ?? '' );

        return buildIconPills( field, { text: this._text, checked, coin: this._iconParentChoice( field ) } );
    }

    /**
     * The chosen option of the icon select a field depends on — what the network buttons'
     * count and hint name. Null when the parent is not an icon select: no count, no hint.
     *
     * @param {Object} field
     * @returns {?{code: string}}
     */
    _iconParentChoice( field ) {
        const parent = field.depends_on ? this.schema?.fields?.find( f => f.code === field.depends_on ) : null;
        if ( ! parent || parent.frontend_component !== 'select' || ! parent.icons ) return null;

        const value  = this._readValue( parent ) ?? '';
        const chosen = ( parent.options ?? [] ).find( opt => opt.value === value );
        return { code: chosen ? optionCode( chosen ) : '' };
    }

    /**
     * Build a single checkbox control.
     *
     * @param {Object} field
     * @returns {HTMLElement}
     */
    _buildCheckbox( field ) {
        const label = document.createElement( 'label' );
        label.className = 'schema-checkbox-label';

        const cb = document.createElement( 'input' );
        cb.type  = 'checkbox';
        cb.id    = `schema_field_${ field.code }`;
        cb.name  = `schema_field_${ field.code }`;
        cb.setAttribute( 'data-schema-field', field.code );
        if ( field.required ) cb.required = true;

        label.appendChild( cb );
        label.append( ` ${ field.label }` );
        return label;
    }

    /**
     * @param {string} code
     * @returns {HTMLElement}
     */
    _buildErrorEl( code ) {
        const el     = document.createElement( 'span' );
        el.className = 'field__validation-message';
        el.setAttribute( 'data-error-for', code );
        return el;
    }

    /**
     * Attach blur / input / change listeners to the control.
     *
     * @param {Object}      field
     * @param {HTMLElement} wrapper
     */
    _attachListeners( field, wrapper ) {
        if ( field.frontend_component === 'radio' ) {
            // Listen on all radio inputs inside the group.
            wrapper.querySelectorAll( 'input[type="radio"]' ).forEach( radio => {
                radio.addEventListener( 'change', () => this._applyFieldValidation( field, wrapper, radio ) );
            } );
            return;
        }

        if ( field.frontend_component === 'checkbox' ) {
            const cb = wrapper.querySelector( 'input[type="checkbox"]' );
            if ( cb ) cb.addEventListener( 'change', () => this._applyFieldValidation( field, wrapper, cb ) );
            return;
        }

        const input = wrapper.querySelector( `[data-schema-field="${ field.code }"]` );
        if ( ! input ) return;

        // Typing only clears the state — a phone number is invalid for most of the time it
        // is being typed. Leaving the field is when the player thinks it is done, so that
        // is when it is checked, profile-backed (`system`) fields included.
        input.addEventListener( 'input', () => this._clearFieldState( wrapper ) );

        input.addEventListener( 'blur',   () => this._applyFieldValidation( field, wrapper, input ) );
        input.addEventListener( 'change', () => this._applyFieldValidation( field, wrapper, input ) );

        // An icon select's native select never has focus; leaving its button or list is the
        // blur that counts — but not while the list is still open (the window lost focus,
        // or the player is still choosing). Tabbing away closes the list first.
        const iconSelect = input.closest( '.schema-icon-select' );
        if ( iconSelect ) {
            iconSelect.addEventListener( 'focusout', event => {
                if ( ! iconSelect.contains( event.relatedTarget ) && ! iconSelect.classList.contains( 'is-open' ) ) {
                    this._applyFieldValidation( field, wrapper, input );
                }
            } );
        }
    }

    // -------------------------------------------------------------------------
    // Dependent option lists
    //
    // A field with `depends_on` offers only the options whose `parent_values` hold the
    // value currently chosen in that other select/radio field — e.g. the networks of the
    // chosen coin. It is shown once there is at least one such option and hidden, unsent
    // and unvalidated otherwise. A parent that is missing, not a choice, or part of a
    // loop never activates it: the field fails closed rather than offering everything.
    // PHP applies the same rule on submit (apply_schema_and_auto_params).
    // -------------------------------------------------------------------------

    /**
     * Map parents to their dependent fields, listen for parent changes, and settle the
     * first paint from every independent field down.
     */
    _wireDependencies() {
        this.dependents = new Map();

        this.schema.fields.forEach( field => {
            if ( ! field.depends_on ) return;
            const list = this.dependents.get( field.depends_on ) ?? [];
            list.push( field );
            this.dependents.set( field.depends_on, list );
        } );

        if ( ! this.dependents.size ) return;

        // On the wrapper, not the control: a dependent parent's control is rebuilt each
        // time its own parent changes, and the wrapper is what survives that. A change by
        // the player starts the dependants over — a network picked for one coin is not
        // carried silently to the next.
        this.dependents.forEach( ( children, parentCode ) => {
            this._wrapperFor( parentCode )?.addEventListener( 'change', () => this._syncDependents( parentCode, new Set(), true ) );
        } );

        // Starting from the independent fields means a seeded or `default` value narrows its
        // dependants on first paint, and a field caught in a loop is never reached.
        this.schema.fields
            .filter( field => ! field.depends_on )
            .forEach( field => this._syncDependents( field.code ) );
    }

    /**
     * Re-filter the fields that depend on `parentCode`, then theirs in turn.
     *
     * @param {string}      parentCode
     * @param {Set<string>} visited    Codes already settled in this pass.
     * @param {boolean}     reset      Drop the dependants' current choices (the player
     *                                 changed the parent) instead of keeping valid ones.
     */
    _syncDependents( parentCode, visited = new Set(), reset = false ) {
        if ( visited.has( parentCode ) ) return;
        visited.add( parentCode );

        const children = this.dependents.get( parentCode );
        if ( ! children ) return;

        const parent = this.schema.fields.find( f => f.code === parentCode );
        const value  = this._isChoice( parent ) && this._isActive( parentCode )
            ? ( this._readValue( parent ) ?? '' )
            : '';

        children.forEach( child => {
            const applicable = value === ''
                ? []
                : ( child.options ?? [] ).filter( opt => ( opt.parent_values ?? [] ).includes( value ) );

            this._applyDependentOptions( child, applicable, reset );
            this._syncDependents( child.code, visited, reset );
        } );
    }

    /**
     * Show a dependent field with just these options, or hide it when there are none.
     *
     * Which option ends up chosen: the current one if it is still offered (first paint,
     * where it can come from a seed or a `default`), else the only one when there is just
     * one (shown, so the player still sees which network the payment goes over), else a
     * `default`, else none.
     *
     * @param {Object}   field      The dependent field (all its options).
     * @param {Object[]} applicable Options offered for the parent's current value.
     * @param {boolean}  reset      Ignore the current choice.
     */
    _applyDependentOptions( field, applicable, reset ) {
        const wrapper = this._wrapperFor( field.code );
        const group   = wrapper?.parentElement;
        if ( ! wrapper || ! group ) return;

        const current = reset ? '' : ( this._readValue( field ) ?? '' );
        const chosen  = applicable.some( opt => opt.value === current )
            ? current
            : ( 1 === applicable.length
                ? applicable[ 0 ].value
                : ( applicable.find( opt => opt.default )?.value ?? '' ) );

        // Rebuilt through the same builders as any other field, on a narrowed copy. Only the
        // control is swapped — the wrapper keeps its floating label and error element.
        const control = this._buildControl( { ...field, options: applicable } );
        wrapper.querySelector( `[data-schema-control="${ field.code }"]` )?.replaceWith( control );
        this._choose( field, control, chosen );
        this._attachListeners( field, wrapper );
        this._clearFieldState( wrapper );

        if ( ! applicable.length ) {
            this._setGroupActive( group, false );
            return;
        }

        this._setGroupActive( group, true );
        new FloatingLabel( group );
    }

    /**
     * Put a value into a freshly built select or radio group ('' = nothing chosen).
     *
     * @param {Object}      field
     * @param {HTMLElement} control
     * @param {string}      value
     */
    _choose( field, control, value ) {
        if ( field.frontend_component === 'radio' ) {
            control.querySelectorAll( 'input[type="radio"]' ).forEach( radio => {
                radio.checked = radio.value === value;
            } );
        } else {
            // '' lands on the disabled "Select …" placeholder, as on a fresh render. An icon
            // select's control wraps its native select.
            const select = control.matches( 'select' ) ? control : control.querySelector( 'select' );
            if ( select ) select.value = value;
        }
        // The value changed without an event; an icon control redraws itself from it.
        control._schemaSync?.();
    }

    /**
     * Put focus on a field that failed validation: an icon select's button (its native
     * select is hidden), a radio group's first or chosen radio, or the input itself.
     *
     * @param {HTMLElement|null} wrapper
     * @param {HTMLElement}      input   The field's [data-schema-field] element.
     */
    _focusField( wrapper, input ) {
        const target = wrapper?.querySelector( '[data-schema-focus]' )
            ?? ( input.matches( 'input, select, textarea' )
                ? input
                : input.querySelector( 'input:checked' ) ?? input.querySelector( 'input' ) );
        target?.focus();
    }

    /**
     * @param {HTMLElement} group
     * @param {boolean}     active
     */
    _setGroupActive( group, active ) {
        group.style.display = active ? '' : 'none';
        if ( active ) {
            delete group.dataset.schemaInactive;
        } else {
            group.dataset.schemaInactive = 'true';
        }
    }

    /**
     * Whether a field takes part in this payment. Only a dependent field waiting for its
     * parent is ever inactive.
     *
     * @param {string} code
     * @returns {boolean}
     */
    _isActive( code ) {
        return this._wrapperFor( code )?.parentElement?.dataset.schemaInactive !== 'true';
    }

    /**
     * @param {Object|undefined} field
     * @returns {boolean}
     */
    _isChoice( field ) {
        return field?.frontend_component === 'select' || field?.frontend_component === 'radio';
    }

    /**
     * @param {string} code
     * @returns {HTMLElement|null}
     */
    _wrapperFor( code ) {
        return this.container?.querySelector( `[data-schema-field-wrapper="${ code }"]` ) ?? null;
    }

    /**
     * A field's current value, trimmed; null when it is not rendered.
     *
     * @param {Object} field
     * @returns {string|null}
     */
    _readValue( field ) {
        const wrapper = this._wrapperFor( field.code );
        if ( ! wrapper ) return null;

        // The full number, "+14165551234", not the masked national one the input shows.
        const phone = this.phoneFields.get( field.code );
        if ( phone ) return phone.value();

        if ( field.frontend_component === 'radio' ) {
            const checked = wrapper.querySelector( 'input[type="radio"]:checked' );
            return checked ? checked.value : '';
        }
        if ( field.frontend_component === 'checkbox' ) {
            const cb = wrapper.querySelector( 'input[type="checkbox"]' );
            return cb?.checked ? '1' : '';
        }
        const input = wrapper.querySelector( `[data-schema-field="${ field.code }"]` );
        return ( input?.value ?? '' ).trim();
    }

    // -------------------------------------------------------------------------
    // Validation
    // -------------------------------------------------------------------------

    /**
     * Run all rules for a field and return the first error message, or ''.
     * Pure — no DOM side-effects.
     *
     * @param {Object} field
     * @param {string} value  Already trimmed.
     * @returns {string}
     */
    _getFieldError( field, value ) {
        if ( field.required && ! value ) {
            return this._message( field, 'required' );
        }

        if ( ! value ) return '';

        if ( field.min_length && value.length < field.min_length ) {
            return this._message( field, 'min_length' );
        }

        if ( field.max_length && value.length > field.max_length ) {
            return this._message( field, 'max_length' );
        }

        if ( field.regex ) {
            try {
                if ( ! new RegExp( field.regex ).test( value ) ) {
                    return this._message( field, 'regex' );
                }
            } catch {
                // Malformed regex — backend will catch it.
            }
        }

        return '';
    }

    /**
     * Error text for a failed rule. The server sends `messages` already resolved to the
     * page language (Payment_Field_Messages::localise_schema) — the same text its own
     * check returns. The wording below is a copy of the PHP default, used only if no
     * text reached the browser.
     *
     * @param {Object} field
     * @param {'required'|'min_length'|'max_length'|'regex'} rule
     * @returns {string}
     */
    _message( field, rule ) {
        const text = field.messages?.[ rule ];
        if ( typeof text === 'string' && text ) return text;

        switch ( rule ) {
            case 'required':   return `${ field.label } is required.`;
            case 'min_length': return `${ field.label } must be at least ${ field.min_length } characters.`;
            case 'max_length': return `${ field.label } must be no more than ${ field.max_length } characters.`;
            default:           return `${ field.label } contains an invalid value.`;
        }
    }

    /**
     * Validate a field, update its visual state, and return any error message.
     *
     * @param {Object}      field
     * @param {HTMLElement} wrapper
     * @param {HTMLElement} input
     * @returns {string}  Error message, or '' if valid.
     */
    _applyFieldValidation( field, wrapper, input ) {
        // The mobile number keeps registration's texts and kinds, amber warnings included,
        // so its result goes to the wrapper as it is. It still has to pass the schema's own
        // rules (SchemaPhoneField checks them), which the server repeats.
        const phone = this.phoneFields.get( field.code );
        if ( phone ) {
            const result = phone.validate();
            applyFieldState( wrapper, result );
            return false === result.valid ? result.message : '';
        }

        let value;
        if ( field.frontend_component === 'radio' ) {
            const checked = wrapper.querySelector( 'input[type="radio"]:checked' );
            value = checked ? checked.value : '';
        } else if ( field.frontend_component === 'checkbox' ) {
            const cb = wrapper.querySelector( 'input[type="checkbox"]' );
            value = cb?.checked ? '1' : '';
        } else {
            value = ( input.value || '' ).trim();
        }

        const error = this._getFieldError( field, value );

        if ( error ) {
            this._setFieldState( wrapper, 'error', error );
        } else if ( value ) {
            this._setFieldState( wrapper, 'success' );
        } else {
            this._clearFieldState( wrapper );
        }

        return error;
    }

    /**
     * Set wrapper to 'error' or 'success' state.
     *
     * @param {HTMLElement} wrapper
     * @param {'error'|'success'} state
     * @param {string} [message]
     */
    _setFieldState( wrapper, state, message = '' ) {
        if ( state === 'error' ) {
            applyFieldState( wrapper, { valid: false, message: message, type: 'error' } );
        } else {
            applyFieldState( wrapper, { valid: true, message: '', type: 'valid' } );
        }
    }

    /**
     * Remove all validation state from a wrapper.
     *
     * @param {HTMLElement} wrapper
     */
    _clearFieldState( wrapper ) {
        clearFieldStateDOM( wrapper );
    }

    /**
     * Remove validation state from every rendered wrapper.
     */
    _clearAllErrors() {
        if ( ! this.container ) return;
        this.container
            .querySelectorAll( '[data-schema-field-wrapper]' )
            .forEach( wrapper => this._clearFieldState( wrapper ) );
    }
}

/**
 * Backwards-compatible alias — the withdrawal flow controller imports this name.
 * New code (e.g. deposit flow) should import SchemaRenderer directly.
 */
export { SchemaRenderer as WithdrawalSchemaRenderer };
